import { makeStyles } from "@mui/styles";
import { Box, Typography } from "@mui/material";
import { SingleVideoContainer } from "./SingleVideoContainer";
import { GroupVideoContainer } from "./GroupVideoContainer";
import { SkillVideoModal } from "../SkillVideoModal";
import React, { useContext, useState } from "react";
import {
  CourseStructureCtx,
  SectionItemsEntity,
  SkillDetails,
  UserCtx,
} from "../../../interfaces";
import { UserContext } from "../../../context/UserContext";
import { useHistory } from "react-router-dom";
import { generateGtmEvent } from "../../../Helpers";
import { useDidMountEffect } from "../../../hooks/useDidMount";
import { IconButtonOutlined } from "../utils/IconButtonOutlined";
import { ReactComponent as IconChevronLeft } from "../../../assets/IconChevronLeft.svg";
import { ReactComponent as IconChevronRight } from "../../../assets/IconChevronRight.svg";
import { CourseStructureContext } from "../../../context/CourseStructureContext";

const CAROUSEL_MAX_WIDTH = 912;

const useStyles = makeStyles((theme) => ({
  controlsButton: {
    width: 54,
    height: 54,
    position: "absolute",
  },
  previousButton: {
    left: -10,
    top: "calc(50% - 30px)",
  },
  nextButton: {
    right: -10,
    top: "calc(50% - 30px)",
  },
}));

interface CarouselItemProps {
  containerName: string;
  items: any[];

  containerType: string;

  playlist: any[];

  currentChunk: number;
  currentSlide?: number;
}

const HandleDataLayer = (
  event: string,
  action: string,
  type: string,
  region: string,
  section: string,
  text: string
) => {
  generateGtmEvent({
    event: event,
    action: action,
    name: "onclick",
    type: type,
    region: region,
    section: section,
    text: text,
  });
};

function CarouselItem(props: CarouselItemProps) {
  const history = useHistory();
  let {
    containerName,
    items,
    containerType,
    playlist,
    currentChunk,
    currentSlide = 0,
  } = props;
  const [skillVideoModalOpen, setSkillVideoModalOpen] = useState(false);
  const [playVideoIndex, setPlayVideoIndex] = useState(0);
  const [videoPlayList, setVideoPlayList] = useState<SkillDetails[]>([]);
  const userCtx = useContext(UserContext) as UserCtx;
  const courseStructureCtx = useContext(CourseStructureContext) as CourseStructureCtx;

  useDidMountEffect(() => {
    if (!skillVideoModalOpen) {
      userCtx.setRefreshExplorePageData(true);
    }
  }, [skillVideoModalOpen]);

  return (
    <>
      <Box
        sx={{
          display: "flex",
          position: "relative",
          left: -currentSlide * CAROUSEL_MAX_WIDTH,
          transition: "left 0.25s ease-in-out",
        }}>
        {items.map((item, i) => {
          if (containerType === "single") {
            return (
              <SingleVideoContainer
                onClick={() => {
                  HandleDataLayer(
                    "modal",
                    "open",
                    "click",
                    "main content",
                    `${containerName}`,
                    `${item.title}`
                  );
                  setSkillVideoModalOpen(true);
                  setPlayVideoIndex(currentChunk * 3 + i);
                  setVideoPlayList(playlist);
                }}
                key={i}
                skillVideoDetails={item}
              />
            );
          }
          if (containerType === "group") {
            console.log(item);
            return (
              <GroupVideoContainer
                onClick={() => {
                  HandleDataLayer(
                    "link",
                    "click",
                    "internal link",
                    "main content",
                    `${containerName}`,
                    `${item.name}`
                  );
                  history.push(
                    `/explore/module?courseId=${
                      courseStructureCtx.courseTreeStructure?.getCurrentCourse().id
                    }&moduleId=${item.id}`
                  );
                }}
                key={i}
                groupTitle={item.name}
                locked={item.locked}
              />
            );
          }

          return null;
        })}
      </Box>
      {containerType === "single" && (
        <SkillVideoModal
          open={skillVideoModalOpen}
          skills={videoPlayList}
          onClose={() => setSkillVideoModalOpen(false)}
          showControls={true}
          showBottomNavigation={false}
          playVideoIndex={playVideoIndex}
          gtmSection={containerName}
        />
      )}
    </>
  );
}

function processItemsIntoChunks(videoList: any) {
  const sliderItems: number = videoList.length > 3 ? 3 : videoList.length;
  const items: Array<any> = [];

  for (let i = 0; i < videoList.length; i += sliderItems) {
    if (i % sliderItems === 0) {
      items.push(
        videoList.slice(i, i + sliderItems).map((item: any, index: any) => {
          return item;
        })
      );
    }
  }

  return items;
}

interface VideoSectionCarouselProps {
  videoList: SectionItemsEntity[];
  videoSectionTitle: string;

  CarouselType: "single" | "group";
}

export const VideoSectionCarousel = (props: VideoSectionCarouselProps) => {
  const classes = useStyles();
  let { videoList, videoSectionTitle, CarouselType } = props;
  let chunks;
  const [currentSlide, setCurrentSlide] = useState(0);

  if (videoList.length > 3) {
    chunks = processItemsIntoChunks(videoList);
  } else {
    chunks = [videoList];
  }

  const handleNext = () => {
    if (!chunks.length) return;
    HandleDataLayer(
      "select",
      "click",
      "carousel",
      "main content",
      `${videoSectionTitle}`,
      " right chevron"
    );
    let nextSlide = currentSlide + 1;
    if (nextSlide === chunks.length) {
      nextSlide = 0;
    }
    setCurrentSlide(nextSlide);
  };

  const handlePrevious = () => {
    if (!chunks.length) return;
    HandleDataLayer(
      "select",
      "click",
      "carousel",
      "main content",
      `${videoSectionTitle}`,
      " left chevron"
    );
    let nextSlide = currentSlide - 1;
    if (nextSlide < 0) {
      nextSlide = chunks.length - 1;
    }
    setCurrentSlide(nextSlide);
  };

  return (
    <Box>
      <Typography
        variant="h3"
        sx={{
          fontWeight: "bold",
          marginBottom: "8px",
          marginLeft: "10px",
        }}>
        {videoSectionTitle} ({videoList.length})
      </Typography>
      <Box
        sx={{
          position: "relative",
          marginTop: CarouselType === "group" ? "24px" : "14px",
        }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            width: "100%",
            maxWidth: `${CAROUSEL_MAX_WIDTH}px`,
            overflowX: "hidden",
            overflowY: "hidden",
            ...(CarouselType === "group" && {
              padding: "26px 0px",
              marginTop: "-26px",
            }),
          }}>
          {chunks.map((chunk, i) => (
            <CarouselItem
              containerName={videoSectionTitle}
              key={i}
              currentSlide={currentSlide}
              currentChunk={i}
              items={chunk}
              containerType={CarouselType}
              playlist={videoList}
            />
          ))}
        </Box>
        {currentSlide > 0 && (
          <IconButtonOutlined
            size="medium"
            className={`${classes.controlsButton} ${classes.previousButton}`}
            onClick={handlePrevious}>
            <IconChevronLeft style={{ width: "26px", height: "26px" }} />
          </IconButtonOutlined>
        )}
        {chunks.length && currentSlide < chunks.length - 1 && (
          <IconButtonOutlined
            size="medium"
            className={`${classes.controlsButton} ${classes.nextButton}`}
            onClick={handleNext}>
            <IconChevronRight style={{ width: "26px", height: "26px" }} />
          </IconButtonOutlined>
        )}
      </Box>
    </Box>
  );
};
